/**
 * pubUi.jsx — shared design kit for the public league site
 * ─────────────────────────────────────────────────────────────────
 * Public pages overhaul P3 (2026-06-09). One place for the visual
 * language of /l/:slug so the eleven public pages stay coherent:
 * light surface, deep-navy chrome, league-themable accent (the
 * manager's `theme_color` content block), big confident type,
 * soft-shadow cards. Intentionally self-contained — the public
 * bundle should not pull the authed app's CSS or components.
 */

import { Link } from 'react-router-dom';

// ─── Theme ───────────────────────────────────────────────────────

export const NAVY = '#0b1220';
export const INK = '#101730';
export const MUTED = '#5b6478';
export const LINE = '#e7eaf3';
export const SURFACE = '#f6f8fc';

/** Sanitize the manager-provided hex; fall back to Game Day blue. */
export function themeAccent(contentBlocks) {
  const raw = (contentBlocks?.theme_color || '').trim();
  return /^#[0-9a-f]{6}$/i.test(raw) ? raw : '#0a6cff';
}

/** Quick relative-luminance check so text on the accent stays readable. */
export function accentText(accent) {
  const n = parseInt(accent.slice(1), 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return (0.299 * r + 0.587 * g + 0.114 * b) > 160 ? '#0b1220' : '#ffffff';
}

// ─── Layout primitives ───────────────────────────────────────────

export const container = {
  maxWidth: 1140, margin: '0 auto', padding: '0 20px',
};

export function Section({ eyebrow, title, action, children, style }) {
  return (
    <section style={{ marginBottom: 44, ...style }}>
      {(eyebrow || title) && (
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <div>
            {eyebrow && (
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: MUTED, marginBottom: 4 }}>
                {eyebrow}
              </div>
            )}
            {title && (
              <h2 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em', margin: 0, color: INK }}>
                {title}
              </h2>
            )}
          </div>
          {action || null}
        </div>
      )}
      {children}
    </section>
  );
}

export function Card({ children, style, hover = false }) {
  return (
    <div
      style={{
        background: '#fff',
        border: `1px solid ${LINE}`,
        borderRadius: 16,
        boxShadow: '0 1px 2px rgba(16,23,48,0.05)',
        transition: hover ? 'transform .12s ease, box-shadow .12s ease' : undefined,
        ...style,
      }}
      onMouseEnter={hover ? (e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(16,23,48,0.10)';
      } : undefined}
      onMouseLeave={hover ? (e) => {
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.boxShadow = '0 1px 2px rgba(16,23,48,0.05)';
      } : undefined}
    >
      {children}
    </div>
  );
}

export function Chip({ children, color = MUTED, bg = SURFACE }) {
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 999,
      fontSize: 11, fontWeight: 800, letterSpacing: '0.04em',
      textTransform: 'uppercase', color, background: bg,
    }}>
      {children}
    </span>
  );
}

export function CtaButton({ to, href, accent, children, ghost = false, onClick, small = false }) {
  const style = {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: small ? '8px 14px' : '12px 22px',
    borderRadius: 12,
    fontWeight: 800, fontSize: small ? 13 : 15,
    textDecoration: 'none', cursor: 'pointer',
    border: ghost ? `2px solid ${accent}` : '2px solid transparent',
    background: ghost ? 'transparent' : accent,
    color: ghost ? accent : accentText(accent),
    whiteSpace: 'nowrap',
  };
  if (to)   return <Link to={to} style={style} onClick={onClick}>{children}</Link>;
  if (href) return <a href={href} style={style} onClick={onClick}>{children}</a>;
  return <button type="button" style={style} onClick={onClick}>{children}</button>;
}

export function EmptyState({ icon = '🏒', children }) {
  return (
    <Card style={{ padding: '40px 24px', textAlign: 'center', color: MUTED, fontSize: 14 }}>
      <div style={{ fontSize: 30, marginBottom: 8 }}>{icon}</div>
      {children}
    </Card>
  );
}

// ─── Content helpers ─────────────────────────────────────────────
// Markdown rendering: pages import renderMarkdown from
// utils/tinyMarkdown.jsx — the established sanitized React-element
// renderer the public register page already uses (no second
// implementation here; one sanitizer to audit).

export function formatDateNice(iso) {
  try {
    const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

export function formatTimeNice(t) {
  if (!t) return '';
  const [h, m] = String(t).split(':').map(Number);
  const d = new Date(); d.setHours(h || 0, m || 0, 0, 0);
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/** Score line for a finished game from the viewing team's side. */
export function resultBadge(game, teamId) {
  if (game.home_score == null || game.away_score == null) return null;
  const mine   = game.home_team_id === teamId ? game.home_score : game.away_score;
  const theirs = game.home_team_id === teamId ? game.away_score : game.home_score;
  const label = mine > theirs ? 'W' : mine < theirs ? 'L' : 'T';
  const color = label === 'W' ? '#10883f' : label === 'L' ? '#c0312f' : MUTED;
  return { label: `${label} ${mine}–${theirs}`, color };
}
